import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { Activity, ChevronDown, Bell } from 'lucide-react';
import WhatsNewModal from './components/WhatsNewModal';
import { CURRENT_VERSION } from './changelog';
import { Toaster } from 'react-hot-toast';
import ReadyApiPage from './ReadyApi.js';
import RobotFrameworkPage from './RobotFramework.js';
import TestsOverviewNewPage from './TestsOverviewNew.js';
import AgentiquePage from './Agentique.js';
import OsintPage from './Osint.js';
import DataPreparationPage from './DataPreparation.js';
import DataExplorationPage from './DataExploration.js';
import InvestigationPage from './Investigation.js';
import FrameworkPage from './Framework.js';
import BackbonePage from './Backbone.js';
import KpisPage from './KpisPage.js';
import DeploymentsPage from './Deployments.js';
import ApiCoveragePage from './ApiCoverage.js';
import FunctionalCoveragePage from './FunctionalCoverage.js';
import LogIntelligencePage from './LogIntelligence.js';
import Login from './Login';
import ReleasesPage from './ReleasesPage';
import azureDevOpsService from './services/azureDevOpsService';


function NavHeader({ onLogout }) {
  const location = useLocation();
  const p = location.pathname;
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [hasNew, setHasNew] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem('whats-new-seen-version');
    if (seen !== CURRENT_VERSION) {
      setHasNew(true);
      const t = setTimeout(() => setShowWhatsNew(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const openWhatsNew = () => {
    setShowWhatsNew(true);
    localStorage.setItem('whats-new-seen-version', CURRENT_VERSION);
    setHasNew(false);
  };

  const closeWhatsNew = () => {
    setShowWhatsNew(false);
    localStorage.setItem('whats-new-seen-version', CURRENT_VERSION);
    setHasNew(false);
  };

  const navCls = (prefix) =>
    p === prefix || p.startsWith(prefix + '/')
      ? 'text-white font-semibold'
      : 'text-slate-300 hover:text-white transition-colors font-medium';

  const dropCls = (prefix) =>
    p === prefix || p.startsWith(prefix + '/')
      ? 'flex items-center gap-1 px-4 py-2 bg-blue-50 text-blue-700 font-semibold'
      : 'flex items-center gap-1 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600';

  return (
    <header className="bg-slate-900 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-3">
              <Activity className="w-8 h-8 text-blue-400" />
              <h1 className="text-xl font-bold text-white">QA Dashboard</h1>
            </div>
            <nav className="flex space-x-4 items-center">
              <Link to="/tests" className={navCls('/tests')}>Vue globale</Link>
              <div className="relative group">
                <button className={`flex items-center space-x-1 font-medium ${['/innovation','/osint','/datapreparation','/dataexploration','/investigation','/framework','/backbone'].some(x => p.startsWith(x)) ? 'text-white' : 'text-slate-300 hover:text-white'} transition-colors`}>
                  <span>Equipes</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/innovation"       className={dropCls('/innovation') + ' first:rounded-t-md'}>
                    {p.startsWith('/innovation') && <span className="text-blue-600 text-xs">✓</span>}Innovation
                  </Link>
                  <Link to="/osint"            className={dropCls('/osint')}>
                    {p.startsWith('/osint') && <span className="text-blue-600 text-xs">✓</span>}OSINT
                  </Link>
                  <Link to="/datapreparation"  className={dropCls('/datapreparation')}>
                    {p.startsWith('/datapreparation') && <span className="text-blue-600 text-xs">✓</span>}Data Preparation
                  </Link>
                  <Link to="/dataexploration"  className={dropCls('/dataexploration')}>
                    {p.startsWith('/dataexploration') && <span className="text-blue-600 text-xs">✓</span>}Data Exploration
                  </Link>
                  <Link to="/investigation"    className={dropCls('/investigation')}>
                    {p.startsWith('/investigation') && <span className="text-blue-600 text-xs">✓</span>}Investigation
                  </Link>
                  <Link to="/framework/back"   className={dropCls('/framework/back')}>
                    {p.startsWith('/framework/back') && <span className="text-blue-600 text-xs">✓</span>}Framework Back
                  </Link>
                  <Link to="/framework/front"  className={dropCls('/framework/front')}>
                    {p.startsWith('/framework/front') && <span className="text-blue-600 text-xs">✓</span>}Framework Front
                  </Link>
                  <Link to="/backbone"         className={dropCls('/backbone') + ' last:rounded-b-md'}>
                    {p.startsWith('/backbone') && <span className="text-blue-600 text-xs">✓</span>}Backbone
                  </Link>
                </div>
              </div>
              <div className="relative group">
                <button className={`flex items-center space-x-1 font-medium ${p.startsWith('/kpis') ? 'text-white' : 'text-slate-300 hover:text-white'} transition-colors`}>
                  <span>KPIs</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/kpis/generals"         className={dropCls('/kpis/generals') + ' first:rounded-t-md'}>Générals</Link>
                  <Link to="/kpis/qa"               className={dropCls('/kpis/qa')}>QA</Link>
                  <Link to="/kpis/squad-socle-front" className={dropCls('/kpis/squad-socle-front')}>Squad Socle Front</Link>
                  <Link to="/kpis/demandes-client"  className={dropCls('/kpis/demandes-client') + ' last:rounded-b-md'}>Demandes client</Link>
                </div>
              </div>
              <div className="relative group">
                <button className={`flex items-center space-x-1 font-medium ${p.startsWith('/releases') ? 'text-white' : 'text-slate-300 hover:text-white'} transition-colors`}>
                  <span>Releases</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className="absolute left-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/releases/25-2"   className={dropCls('/releases/25-2') + ' first:rounded-t-md'}>25.2</Link>
                  <Link to="/releases/26-1"   className={dropCls('/releases/26-1')}>26.1.0</Link>
                  <Link to="/releases/26-1-1" className={dropCls('/releases/26-1-1')}>26.1.1</Link>
                  <Link to="/releases/26-2-0" className={dropCls('/releases/26-2-0') + ' last:rounded-b-md'}>26.2.0</Link>
                </div>
              </div>
              <Link to="/deployments" className={navCls('/deployments')}>Déploiements</Link>
              <Link to="/log-intelligence" className={navCls('/log-intelligence')}>Log Intelligence</Link>
              <div className="relative group">
                <button className={`flex items-center space-x-1 font-medium ${p.startsWith('/couverture') ? 'text-white' : 'text-slate-300 hover:text-white'} transition-colors`}>
                  <span>Couverture</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className="absolute left-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/couverture/api" className={dropCls('/couverture/api') + ' first:rounded-t-md'}>API</Link>
                  <Link to="/couverture/fonctionnelle" className={dropCls('/couverture/fonctionnelle') + ' last:rounded-b-md'}>Fonctionnelle</Link>
                </div>
              </div>
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={openWhatsNew}
              className="relative p-1.5 text-slate-400 hover:text-white transition-colors"
              title="Nouveautés"
            >
              <Bell className="w-5 h-5" />
              {hasNew && (
                <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-slate-900" />
              )}
            </button>
            <div className="text-sm text-slate-400 whitespace-nowrap">
              <span className="font-medium text-slate-300">v{CURRENT_VERSION}</span> <span className="text-slate-600">•</span> <span className="text-xs text-slate-500">02/11/2026</span>
            </div>
            <button onClick={onLogout} className="text-sm px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium">
              Se déconnecter
            </button>
          </div>
          <WhatsNewModal open={showWhatsNew} onClose={closeWhatsNew} />
        </div>
      </div>
    </header>
  );
}

function App() {
  const [pat, setPat] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Always validate stored PAT before entering the app
    const validateStoredPat = async () => {
      try {
        const existing = azureDevOpsService.getPat();
        if (!existing) {
          setPat(null);
          return;
        }

        const validation = await azureDevOpsService.validatePat(existing);
        if (validation.valid) {
          setPat(existing);
        } else {
          azureDevOpsService.clearPat();
          setPat(null);
        }
      } catch (error) {
        console.error('Erreur validation PAT au démarrage:', error);
        azureDevOpsService.clearPat();
        setPat(null);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    validateStoredPat();
  }, []); // Ne s'exécute qu'au montage initial

  useEffect(() => {
    const onInvalidAuth = () => {
      setPat(null);
      setIsCheckingAuth(false);
    };

    window.addEventListener('azdo-auth-invalid', onInvalidAuth);
    return () => window.removeEventListener('azdo-auth-invalid', onInvalidAuth);
  }, []);

  const handleLogin = () => {
    setPat(azureDevOpsService.getPat());
  };

  const handleLogout = () => {
    // Clear PAT from service and return to login screen
    try {
      azureDevOpsService.clearPat();
    } catch (e) {
      console.warn('Erreur lors du clearPat:', e);
    }
    setPat(null);
  };

  if (isCheckingAuth) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50">Vérification du PAT...</div>;
  }

  if (!pat) {
    return <Login onLogin={handleLogin} />;
  }
  return (
    <Router>
      <div className="min-h-screen bg-gray-100">
        {/* Global toaster so toasts are always mounted at app root */}
        <Toaster position="top-right" />
        <NavHeader onLogout={handleLogout} />

        {/* Routing */}
        <Routes>
          <Route path="/" element={<Navigate to="/tests" />} />
          <Route path="/tests" element={<TestsOverviewNewPage />} />
          <Route path="/readyapi/*" element={<ReadyApiPage />} />
          <Route path="/robotframework/*" element={<RobotFrameworkPage />} />
          <Route path="/innovation/*" element={<AgentiquePage />} />
          <Route path="/osint/*" element={<OsintPage />} />
          <Route path="/datapreparation/*" element={<DataPreparationPage />} />
          <Route path="/dataexploration/*" element={<DataExplorationPage />} />
          <Route path="/framework/*" element={<FrameworkPage />} />
          <Route path="/investigation/*" element={<InvestigationPage />} />
          <Route path="/backbone/*" element={<BackbonePage />} />
          <Route path="/kpis/:theme" element={<KpisPage />} />
          <Route path="/kpis" element={<Navigate to="/kpis/generals" />} />
          <Route path="/releases/:release" element={<ReleasesPage />} />
          <Route path="/releases" element={<Navigate to="/releases/26-1" />} />
          <Route path="/deployments" element={<DeploymentsPage />} />
          <Route path="/log-intelligence" element={<LogIntelligencePage />} />
          {/* URL directe vers une erreur, pour qu'un ticket Azure DevOps
              puisse pointer dessus. Meme page : le lien ouvre le detail de
              cette signature par-dessus le catalogue, et le fermer revient a
              la liste. */}
          <Route path="/log-intelligence/signature/:signatureId" element={<LogIntelligencePage />} />
          <Route path="/couverture/api" element={<ApiCoveragePage />} />
          <Route path="/couverture/fonctionnelle" element={<FunctionalCoveragePage />} />
          <Route path="/couverture" element={<Navigate to="/couverture/api" />} />
        </Routes>
      </div>
    </Router>
  );
}


export default App;
